import { memo, lazy, Suspense, useContext } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import getWindowSize from '../../utils/hooks/getWindowSize'
import useAnimation from '../../utils/hooks/useAnimation'
import antagonistsJson from '../../data/antagonists.json'
import { getAntagonistFromCard } from '../../api/engine'
import OptionsContext from '../../utils/OptionsContext'
import { Button, Plus, ChevronRight } from '..'
import token from '../../assets/svgs/token.png'
import styles from './CardItem.module.scss'
import SkeletonCard from '../Skeletons/SkeletonCard'

const LazyCard = lazy(() => import('../Card/Card'))

const CardItem = memo(
  ({
    card,
    active,
    isApp,
    cardSelectable,
    handleClickOnCard,
    isCollectionViewer,
    onOpenBoost,
    setActiveCardId,
    playCardScenario,
    addCardToHand,
    renderSize,
  }) => {
    const { t } = useTranslation()
    const { shouldReduceMotion } = useContext(OptionsContext).options
    const [animateAdd, triggerAdd] = useAnimation({ scale: 1.3 })
    const [animatePlay, triggerPlay] = useAnimation({ x: 4 })
    const [animateStar, triggerStar] = useAnimation({ scale: 1.2 })
    const { width } = getWindowSize()
    const isMobile = width < 768

    const hoverAnimation = {
      initial: {
        y: 0,
      },
      animate: {
        y: -3,
        transition: {
          type: 'spring',
        },
      },
    }

    const buttonVariants = {
      initial: { y: '-100px', opacity: 0 },
      animate: { y: 0, opacity: 1 },
    }

    const cardVariants = {
      initial: {
        zIndex: 1,
        transition: { delay: 0.2 },
      },
      active: {
        zIndex: 20,
      },
    }

    const borderStyle =
      !isApp && active
        ? { border: `2px solid ${card.color}` }
        : { border: 'none' }
    const listItemClassName = `${active && styles.activeListItem}`
    const cardClassName = !isApp ? `${active && styles.activeCard}` : ''

    function checkIsPlayable(card) {
      return getAntagonistFromCard(antagonistsJson, card)
    }

    return (
      <li key={card.id} className={listItemClassName} style={borderStyle}>
        <motion.div
          layout
          className={cardClassName}
          data-click='outside'
          variants={cardVariants}
          initial='initial'
          animate={active ? 'active' : 'initial'}
        >
          <Suspense fallback={<SkeletonCard size={renderSize(active)} />}>
            <LazyCard
              which={card}
              onClick={cardSelectable ? () => handleClickOnCard(card) : null}
              animation={active && !isMobile ? null : hoverAnimation}
              id={`card-${card.id}`}
              size={renderSize(active)}
              isOpen={active}
              isApp={isApp}
            />
          </Suspense>
          <AnimatePresence>
            {active && cardSelectable && (
              <motion.div
                key={card.id}
                variants={buttonVariants}
                initial='initial'
                animate='animate'
                transition={
                  shouldReduceMotion ? { duration: 0.01 } : { delay: 0.2 }
                }
                aria-controls={`card-${card.id}`}
                className={styles.buttonWrapper}
              >
                {isCollectionViewer && (
                  <div className={styles.collectionBtns}>
                    {checkIsPlayable(card) && (
                      <Button
                        variant='secondary'
                        onClick={() => {
                          onOpenBoost(card)
                          setActiveCardId(null)
                        }}
                        onMouseEnter={triggerStar}
                      >
                        {t('common:carddata:boost')}
                        <motion.span animate={animateStar}>
                          <img src={token} alt='Token' />
                        </motion.span>
                      </Button>
                    )}
                    {checkIsPlayable(card) && (
                      <Button
                        onClick={() => playCardScenario(card)}
                        onMouseEnter={triggerPlay}
                        hasOwnSound
                      >
                        {t('common:carddata:playcard')}
                        <motion.span animate={animatePlay}>
                          <ChevronRight />
                        </motion.span>
                      </Button>
                    )}
                  </div>
                )}
                {!isCollectionViewer && !isApp && (
                  <Button
                    onClick={() => addCardToHand(card)}
                    hasOwnSound
                    onMouseEnter={triggerAdd}
                  >
                    {t('common:carddata:addtohand')}
                    <motion.span animate={animateAdd}>
                      <Plus />
                    </motion.span>
                  </Button>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </li>
    )
  }
)

export default CardItem
